import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import log from 'loglevel'
import { toast } from 'react-toastify'
import {
  Artifact,
  ArtifactType,
} from '@trustero/trustero-api-web/lib/artifact/artifact_pb'
import { ChatMessage } from '@trustero/trustero-api-web/lib/chat/chat_pb'
import {
  WriteArtifactRequest,
  UploadDocRequest,
  SaveArtifactAsEvidenceRequest,
} from '@trustero/trustero-api-web/lib/artifact/service_pb'
import { ArtifactServicePromiseClient } from '@trustero/trustero-api-web/lib/artifact/service_grpc_web_pb'
import { FileUploadBody } from '@trustero/trustero-api-web/lib/common/model_pb'
import { ClientMessageType } from '@trustero/trustero-api-web/lib/tichatapi/agent_pb'
import { useAuthorizedGrpcClient } from 'src/adapter/grpcClient'
import { getBytesPayload } from 'src/Utils/File/file'
import { useArtifactPanelContext } from '../chatpanel/chatPanel.context'
import { OnSend } from '../conversation/chatConversation.hooks'

// Module-level registry of in-flight artifact saves, keyed by artifact ID.
// ChatConfirmActions awaits this before sending a confirmation so the backend
// reads the latest saved value and not the stale placeholder.
const pendingArtifactSaves = new Map<string, Promise<void>>()

export const awaitAllPendingArtifactSaves = (): Promise<void> =>
  Promise.all([...pendingArtifactSaves.values()]).then(() => undefined)

export const useUpdateArtifact = (): ((
  artifact: Artifact,
) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ArtifactServicePromiseClient)

  return useCallback(
    (artifact: Artifact) => {
      const request = new WriteArtifactRequest()
      request.setArtifact(artifact)
      const id = artifact.getId()
      const savePromise = (async () => {
        try {
          await client.updateArtifact(request)
        } catch (error) {
          log.error('Error updating artifact', error)
          toast.error('Error updating artifact')
        }
      })()
      pendingArtifactSaves.set(id, savePromise)
      // Only clear when this specific promise is still the latest save for this ID
      savePromise.finally(() => {
        if (pendingArtifactSaves.get(id) === savePromise) {
          pendingArtifactSaves.delete(id)
        }
      })
      return savePromise
    },
    [client],
  )
}

type UploadDocProps = {
  artifactId: string
  files: {
    file: File
    caption?: string
  }[]
}

export const useUploadDoc = (): ((
  props: UploadDocProps,
) => Promise<Artifact>) => {
  const client = useAuthorizedGrpcClient(ArtifactServicePromiseClient)

  return useCallback(
    async ({ artifactId, files }: UploadDocProps): Promise<Artifact> => {
      try {
        // Convert multiple files to UploadData objects
        const uploadDataList = await Promise.all(
          files.map(async (data) => {
            const fileBytes = await getBytesPayload(data.file)
            return new FileUploadBody()
              .setFileName(data.file.name)
              .setMimeType(data.file.type || 'application/octet-stream')
              .setFile(fileBytes)
              .setCaption(data.caption || '')
          }),
        )

        const request = new UploadDocRequest()
          .setArtifactId(artifactId)
          .setUploadDataList(uploadDataList)

        const response = await client.uploadDoc(request)
        const artifact = response.getArtifact()
        if (!artifact) {
          throw new Error('No artifact returned from upload')
        }
        return artifact
      } catch (error) {
        log.error('Error uploading policy documents:', error)
        toast.error('Failed to upload policy documents')
        throw error
      }
    },
    [client],
  )
}

export type OpenArtifactPanelProps = {
  artifact: Artifact
  onSend?: OnSend
  message?: ChatMessage
}

export const useOpenArtifactPanel = ({
  artifact,
  onSend,
  message,
}: OpenArtifactPanelProps): (() => void) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const {
    setArtifactPanelOpen,
    setSelectedArtifactId,
    setSelectedArtifactMessageId,
    setSelectedArtifactPreview,
    setSelectedArtifactMessagePreview,
    setOnSend,
  } = useArtifactPanelContext()

  const updateArtifactDeepLink = useCallback(
    (artifactId: string) => {
      const newParams = new URLSearchParams(searchParams)
      newParams.set('artifact', artifactId)
      setSearchParams(newParams, { replace: true })
    },
    [searchParams, setSearchParams],
  )

  const clearArtifactDeepLink = useCallback(() => {
    const newParams = new URLSearchParams(searchParams)
    newParams.delete('artifact')
    setSearchParams(newParams, { replace: true })
  }, [searchParams, setSearchParams])

  const sendMessage = useCallback(
    (prompt: string, messageType: ClientMessageType | undefined) => {
      clearArtifactDeepLink()
      setArtifactPanelOpen(false)
      setSelectedArtifactId(null)
      setSelectedArtifactMessageId(null)
      setSelectedArtifactPreview(null)
      setSelectedArtifactMessagePreview(null)
      setOnSend(null)
      onSend?.(prompt, messageType)
    },
    [
      setArtifactPanelOpen,
      setOnSend,
      setSelectedArtifactId,
      setSelectedArtifactMessageId,
      setSelectedArtifactPreview,
      setSelectedArtifactMessagePreview,
      onSend,
      clearArtifactDeepLink,
    ],
  )

  return useCallback(() => {
    setArtifactPanelOpen(true)
    setSelectedArtifactId(artifact.getId())
    setSelectedArtifactMessageId(message?.getId() ?? null)
    setSelectedArtifactPreview(artifact)
    setSelectedArtifactMessagePreview(message ?? null)
    setOnSend(() => sendMessage)
    updateArtifactDeepLink(artifact.getId())
  }, [
    setArtifactPanelOpen,
    setSelectedArtifactId,
    setSelectedArtifactMessageId,
    setSelectedArtifactPreview,
    setSelectedArtifactMessagePreview,
    updateArtifactDeepLink,
    artifact,
    message,
    setOnSend,
    sendMessage,
  ])
}

export const useSaveArtifactAsEvidence = (): ((
  artifactId: string,
  caption: string,
  description?: string,
) => Promise<{ evidenceId: string; evidenceInstanceId: string }>) => {
  const client = useAuthorizedGrpcClient(ArtifactServicePromiseClient)

  return useCallback(
    async (artifactId: string, caption: string, description = '') => {
      const request = new SaveArtifactAsEvidenceRequest()
        .setArtifactId(artifactId)
        .setCaption(caption)
        .setDescription(description)
      const response = await client.saveArtifactAsEvidence(request)
      return {
        evidenceId: response.getEvidenceId(),
        evidenceInstanceId: response.getEvidenceInstanceId(),
      }
    },
    [client],
  )
}

export const useCloseArtifactPanel = (onClose: () => void): (() => void) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const {
    setSelectedArtifactId,
    setSelectedArtifactMessageId,
    setSelectedArtifactPreview,
    setSelectedArtifactMessagePreview,
    setOnSend,
  } = useArtifactPanelContext()
  const clearArtifactDeepLink = useCallback(() => {
    const newParams = new URLSearchParams(searchParams)
    newParams.delete('artifact')
    setSearchParams(newParams, { replace: true })
  }, [searchParams, setSearchParams])

  return useCallback(() => {
    clearArtifactDeepLink()
    setSelectedArtifactId(null)
    setSelectedArtifactMessageId(null)
    setSelectedArtifactPreview(null)
    setSelectedArtifactMessagePreview(null)
    setOnSend(null)
    onClose()
  }, [
    clearArtifactDeepLink,
    onClose,
    setOnSend,
    setSelectedArtifactId,
    setSelectedArtifactMessageId,
    setSelectedArtifactPreview,
    setSelectedArtifactMessagePreview,
  ])
}

export const isArtifactUsable = (artifact?: Artifact): boolean => {
  if (!artifact) return false

  switch (artifact.getType()) {
    case ArtifactType.ARTIFACT_POLICIES:
      return (
        artifact
          .getPoliciesArtifact()
          ?.getPoliciesList()
          .some((policy) =>
            policy
              .getPolicyDocsList()
              .some((doc) => doc.getSelected() && doc.getContentId()),
          ) || false
      )
    case ArtifactType.ARTIFACT_FRAMEWORK_DOC:
      return (
        artifact
          .getFrameworkDocArtifact()
          ?.getFrameworkDocsList()
          .some((doc) => doc.getContentId() && doc.getSelected()) || false
      )
    case ArtifactType.ARTIFACT_FRAMEWORK_SECTION:
      return (
        artifact
          .getFrameworkSectionArtifact()
          ?.getSectionsList()
          .some((section) => section.getSelected()) || false
      )
    case ArtifactType.ARTIFACT_CONTROLS:
      return (
        artifact
          .getControlsArtifact()
          ?.getControlsList()
          .some((control) => control.getSelected()) || false
      )
    case ArtifactType.ARTIFACT_RISKS:
      return (
        artifact
          .getRisksArtifact()
          ?.getRisksList()
          .some((risk) => risk.getSelected()) || false
      )
    case ArtifactType.ARTIFACT_VENDORS:
      return (
        artifact
          .getVendorsArtifact()
          ?.getVendorsList()
          .some((vendor) => vendor.getSelected()) || false
      )
    case ArtifactType.ARTIFACT_SELECTION:
      return (
        artifact
          .getSelectionArtifact()
          ?.getSelectOptionsList()
          .some((option) => option.getSelected()) || false
      )
    case ArtifactType.ARTIFACT_PLAYBOOK_PARAM_INPUT:
      return (
        artifact
          .getPlaybookParamInputArtifact()
          ?.getOptionsList()
          .some((option) => option.getSelected()) || false
      )
    default:
      return true
  }
}

export const useIsArtifactUsable = (artifact?: Artifact): boolean =>
  useMemo(() => isArtifactUsable(artifact), [artifact])
